import apiClient from './apiClient.js'
import { decryptGamePuzzles } from '../utils/encryption.js'

/**
 * Asegura que los puzzles de un juego estén desencriptados
 * @param {Object} gameData - Datos del juego
 * @returns {Object} - Juego con puzzles desencriptados
 */
function ensureDecryptedPuzzles(gameData) {
  if (!gameData) return gameData

  // Si es un solo juego
  if (gameData.data && !Array.isArray(gameData.data)) {
    return {
      ...gameData,
      data: decryptGamePuzzles(gameData.data)
    }
  }

  // Si es una lista de juegos
  if (gameData.data && Array.isArray(gameData.data)) {
    return {
      ...gameData,
      data: gameData.data.map(game => decryptGamePuzzles(game))
    }
  }

  // Si es directamente un juego o lista de juegos
  if (Array.isArray(gameData)) {
    return gameData.map(game => decryptGamePuzzles(game))
  }

  return decryptGamePuzzles(gameData)
}

// Usar el cliente API compartido
const api = apiClient;

/**
 * Servicio para gestionar los juegos
 */
export const gameService = {
  /**
   * Obtiene todos los juegos
   */
  async getAllGames() {
    try {
      const response = await api.get('/games')
      return ensureDecryptedPuzzles(response.data)
    } catch (error) {
      console.error('Error al obtener todos los juegos:', error)
      throw error
    }
  },

  /**
   * Obtiene lista básica de juegos (sin puzzles)
   */
  async getGamesList() {
    try {
      const response = await api.get('/games/list')
      return response.data
    } catch (error) {
      console.error('Error al obtener lista de juegos:', error)
      throw error
    }
  },

  /**
   * Obtiene un juego por ID
   */
  async getGameById(gameId) {
    try {
      const response = await api.get(`/games/${gameId}`)
      return ensureDecryptedPuzzles(response.data)
    } catch (error) {
      console.error(`Error al obtener juego ${gameId}:`, error)
      throw error
    }
  },

  /**
   * Crea un nuevo juego
   */
  async createGame(gameData) {
    try {
      const response = await api.post('/games', gameData)
      return response.data
    } catch (error) {
      console.error('Error al crear juego:', error)
      throw error
    }
  },

  /**
   * Actualiza un juego existente
   */
  async updateGame(gameId, gameData) {
    try {
      const response = await api.put(`/games/${gameId}`, gameData)
      return response.data
    } catch (error) {
      console.error(`Error al actualizar juego ${gameId}:`, error)
      throw error
    }
  },

  /**
   * Elimina un juego
   */
  async deleteGame(gameId) {
    try {
      const response = await api.delete(`/games/${gameId}`)
      return response.data
    } catch (error) {
      console.error(`Error al eliminar juego ${gameId}:`, error)
      throw error
    }
  },

  /**
   * Reordena los juegos según un array de IDs
   */
  async reorderGames(gameIds) {
    try {
      const response = await api.put('/games/reorder', { gameIds })
      return ensureDecryptedPuzzles(response.data)
    } catch (error) {
      console.error('Error al reordenar juegos:', error)
      throw error
    }
  },

  /**
   * Busca juegos por término de búsqueda
   */
  async searchGames(searchTerm, language = 'es', fields = ['name']) {
    try {
      const response = await api.get('/games/search', {
        params: { q: searchTerm, lang: language, fields: fields.join(',') }
      })
      return response.data
    } catch (error) {
      console.error('Error al buscar juegos:', error)
      throw error
    }
  },

  /**
   * Obtiene estadísticas de los juegos
   */
  async getStats() {
    try {
      const response = await api.get('/games/stats')
      return response.data
    } catch (error) {
      console.error('Error al obtener estadísticas:', error)
      throw error
    }
  },

  /**
   * Importa juegos desde datos JSON
   */
  async importGames(gamesData) {
    try {
      const response = await api.post('/games/import', gamesData)
      return response.data
    } catch (error) {
      console.error('Error al importar juegos:', error)
      throw error
    }
  },

  /**
   * Valida datos de juegos antes de importar
   */
  async validateGamesImport(gamesData) {
    try {
      const response = await api.post('/games/import/validate', gamesData)
      return response.data
    } catch (error) {
      console.error('Error al validar importación de juegos:', error)
      throw error
    }
  }
}

export default gameService
